import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, catchError } from 'rxjs/operators';
import { Folder, FolderPlanogram } from 'src/app/models/folder';
import { StoreSearchEntry } from 'src/app/models/store-search-entry';
import { ApiService } from 'src/app/services/api.service';
import { PlanogramApiService } from 'src/app/services/planogram-api.service';
import { ToastService } from 'src/app/services/toasts.service';
import { BulkClonePlanogramRequest, BulkClonePlanogramItem } from 'src/app/requests/bulk-clone-planogram.request';

/** Selection context passed from the home page via router navigation state. */
interface BulkCloneNavState {
  planograms?: FolderPlanogram[];
  rootFolders?: Folder[];
  originFolderId?: string | null;
}

@Component({
  selector: 'app-bulk-clone',
  templateUrl: './bulk-clone.component.html',
  styleUrl: './bulk-clone.component.scss',
  imports: [FormsModule, ReactiveFormsModule, NgbTypeahead],
})
export class BulkCloneComponent implements OnInit {
  planograms: FolderPlanogram[] = [];
  rootFolders: Folder[] = [];
  originFolderId: string | null = null;

  bulkCloneForm: FormGroup;
  flatFolders: { id: string; name: string }[] = [];
  isSubmitting = false;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private apiService: ApiService,
    private planogramApiService: PlanogramApiService,
    private toastService: ToastService,
  ) {
    // Selection is handed over via router state (set by the home page before navigating).
    const navState = (this.router.getCurrentNavigation()?.extras.state ?? history.state) as BulkCloneNavState;
    this.planograms = navState?.planograms ?? [];
    this.rootFolders = navState?.rootFolders ?? [];
    this.originFolderId = navState?.originFolderId ?? null;

    this.bulkCloneForm = this.fb.group({
      store: [null, Validators.required],
      folderId: [''],
    });
  }

  ngOnInit(): void {
    // A direct visit or page refresh carries no selection state - send the user back.
    if (this.planograms.length === 0) {
      this.toastService.show('No planograms selected for bulk clone.', { classname: 'bg-warning text-dark', delay: 3000 });
      this.goBack();
      return;
    }

    this.flatFolders = this.flattenFolders(this.rootFolders);
    this.bulkCloneForm.patchValue({ folderId: this.originFolderId ?? '' });
  }

  /** All selected planograms must share a data source to clone into the same store;
   *  scope the store search to the first one's data source (mirrors single clone). */
  private get dataSourceId(): string {
    return this.planograms[0]?.dataSourceId ?? '';
  }

  private flattenFolders(folders: Folder[], level = 0): { id: string; name: string }[] {
    let flatList: { id: string; name: string }[] = [];
    for (const folder of folders) {
      flatList.push({
        id: folder.guid,
        name: (level > 0 ? '  '.repeat(level) + '↳ ' : '') + folder.name
      });
      if (folder.subFolders) {
        flatList = flatList.concat(this.flattenFolders(folder.subFolders, level + 1));
      }
    }
    return flatList;
  }

  searchStores = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      switchMap(term => this.apiService.searchStores(term, this.dataSourceId).pipe(catchError(() => of([]))))
    );

  storeFormatter = (store: StoreSearchEntry) => store.name;

  /** The newest saved state for a planogram - this is what gets cloned. */
  private latestStateId(planogram: FolderPlanogram): string | null {
    if (!planogram.states || planogram.states.length === 0) return null;
    return [...planogram.states].sort((a, b) =>
      new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
    )[0].guid;
  }

  goBack(): void {
    this.router.navigate(this.originFolderId ? ['/folder', this.originFolderId] : ['/']);
  }

  async submit(): Promise<void> {
    if (this.bulkCloneForm.invalid || this.isSubmitting) return;

    const val = this.bulkCloneForm.value;
    const store = val.store as StoreSearchEntry;

    const items: BulkClonePlanogramItem[] = [];
    for (const planogram of this.planograms) {
      const stateId = this.latestStateId(planogram);
      if (!stateId) continue; // no saved state means there's nothing to clone
      items.push({ planogramId: planogram.guid, stateId, name: null });
    }

    if (items.length === 0) {
      this.toastService.show('None of the selected planograms have a saved state to clone.', { classname: 'bg-warning text-dark', delay: 4000 });
      return;
    }

    const request: BulkClonePlanogramRequest = {
      items,
      storeId: store.storeGuid,
      merchId: null,
      folderId: val.folderId || null,
    };

    this.isSubmitting = true;
    try {
      const result = await this.planogramApiService.bulkClonePlanogram(request);
      const succeeded = result.results.filter(r => r.success).length;
      const failed = result.results.length - succeeded;

      if (failed === 0) {
        this.toastService.show(`${succeeded} planogram(s) cloned successfully.`, { classname: 'bg-success text-light', delay: 3500 });
      } else {
        this.toastService.show(`${succeeded} cloned, ${failed} failed (e.g. a different data source to the target store).`, { classname: 'bg-warning text-dark', delay: 6000 });
      }

      const destination = val.folderId || this.originFolderId;
      this.router.navigate(destination ? ['/folder', destination] : ['/']);
    } catch (err) {
      console.error(err);
      this.toastService.show('Bulk clone failed.', { classname: 'bg-danger text-light', delay: 4000 });
    } finally {
      this.isSubmitting = false;
    }
  }
}
